import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { GLOBAL_IMAGES } from "../assets/asset";
import { useSubscription } from "../context/SubscriptionContext";
import GlobalSubscriptionBanner from "../components/subscription/GlobalSubscriptionBanner";
import { logout } from "../features/auth/user.reducer";

const planCards = [
  {
    name: "Monthly Basic",
    planType: "basic",
    interval: "monthly",
    currency: "AUD",
    description:
      "A monthly subscription is an ideal choice for you in various situations:",
    benefits: [
      "Flexibility",
      "Short-Term Learning Goals",
      "Budget Constraints",
      "Appraisal Period",
    ],
    price: "A$4.99",
    billingText: "/ month",
  },
  {
    name: "Monthly Pro",
    planType: "pro",
    interval: "monthly",
    currency: "AUD",
    description:
      "A monthly subscription is an ideal choice for you in various situations:",
    benefits: [
      "Flexibility",
      "Short-Term Learning Goals",
      "Budget Constraints",
      "Appraisal Period",
    ],
    price: "A$9.99",
    billingText: "/ month",
  },
  {
    name: "Yearly Basic",
    planType: "basic",
    interval: "yearly",
    currency: "AUD",
    description:
      "A yearly subscription is an ideal choice for you in various situations:",
    benefits: [
      "Cost Efficiency",
      "Long-Term Commitment",
      "Reduced Hassle",
      "Consistent Learning",
    ],
    price: "A$59.99",
    billingText: "/ year",
  },
  {
    name: "Yearly Pro",
    planType: "pro",
    interval: "yearly",
    currency: "AUD",
    description:
      "A yearly subscription is an ideal choice for you in various situations:",
    benefits: [
      "Cost Efficiency",
      "Long-Term Commitment",
      "Reduced Hassle",
      "Consistent Learning",
    ],
    price: "A$119.99",
    billingText: "/ year",
  },
];

const SUPPORTED_PLAN_KEYS = new Set([
  "basic_monthly",
  "pro_monthly",
  "basic_yearly",
  "pro_yearly",
]);

const normalizeInterval = (value = "") => {
  const normalized = String(value).trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (normalized === "month") return "monthly";
  if (normalized === "year") return "yearly";
  return normalized;
};

const normalizePlanType = (value = "") => {
  const normalized = String(value).trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (normalized === "basic" || normalized === "pro") return normalized;
  return "";
};

const normalizePlanKey = (value = "") => {
  const normalized = String(value).trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (!normalized) return "";
  if (SUPPORTED_PLAN_KEYS.has(normalized)) return normalized;

  const [partA, partB] = normalized.split("_");
  if (!partA || !partB) return "";

  const normalizedA = normalizeInterval(partA);
  const normalizedB = normalizeInterval(partB);
  const maybeSwapped = `${normalizePlanType(normalizedB)}_${normalizedA}`;

  if (SUPPORTED_PLAN_KEYS.has(maybeSwapped)) return maybeSwapped;
  return "";
};

const SubscriptionPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const hasHandledReturnRef = useRef(false);
  const paymentPlansRef = useRef(null);

  const [activeButtonKey, setActiveButtonKey] = useState("");
  const [frontendError, setFrontendError] = useState("");
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);

  const redirectPath = location?.state?.from || "/yearsdashboard";
  const shouldAutoRedirect = Boolean(location?.state?.from);
  const isReturnFromCheckout = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return (
      ["success", "redirect_status", "session_id", "setup_intent"].some((key) =>
        searchParams.has(key)
      ) || ["success", "cancel"].includes(searchParams.get("subscription"))
    );
  }, [location.search]);

  const {
    subscription,
    hasAccess,
    isChecking,
    checkSubscription,
    createSubscription,
    isCreating,
    normalizedPlanType,
  } = useSubscription();

  const trialText = useMemo(() => {
    if (subscription?.subscription_status === "trialing") {
      const daysLeft = Number(subscription?.days_left || 0);
      return `Trial active, ${daysLeft} days left`;
    }
    if (subscription?.subscription_status === "active") {
      return "Subscription active";
    }
    return "Subscription required";
  }, [subscription]);

  const currentPlanKey = useMemo(() => {
    const directCandidates = [
      subscription?.plan,
      subscription?.subscription_plan,
      subscription?.plan_name,
      subscription?.plan_key,
      subscription?.price_plan,
    ];

    for (const candidate of directCandidates) {
      const normalized = normalizePlanKey(candidate);
      if (normalized) return normalized;
    }

    const planType =
      normalizedPlanType ||
      normalizePlanType(subscription?.plan_type || subscription?.planType || "");
    const interval = normalizeInterval(
      subscription?.interval ||
        subscription?.billing_interval ||
        subscription?.billingInterval ||
        subscription?.cycle ||
        subscription?.period ||
        ""
    );

    const composed = `${planType}_${interval}`;
    return SUPPORTED_PLAN_KEYS.has(composed) ? composed : "";
  }, [subscription, normalizedPlanType]);

  const resetCheckoutUiState = useCallback(() => {
    setIsRedirecting(false);
    setActiveButtonKey("");
    try {
      sessionStorage.removeItem("pendingStripeCheckout");
      sessionStorage.removeItem("pendingStripeCheckoutAt");
    } catch (_) {
      // Ignore storage errors.
    }
  }, []);

  useEffect(() => {
    if (hasAccess && shouldAutoRedirect) {
      navigate(redirectPath, { replace: true });
    }
  }, [hasAccess, shouldAutoRedirect, navigate, redirectPath]);

  useEffect(() => {
    if (isCreating) return;
    if (!isReturnFromCheckout) {
      resetCheckoutUiState();
    }
  }, [isCreating, isReturnFromCheckout, resetCheckoutUiState]);

  useEffect(() => {
    const handlePageShow = () => {
      if (!isReturnFromCheckout && !isCreating) {
        resetCheckoutUiState();
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [isReturnFromCheckout, isCreating, resetCheckoutUiState]);

  useEffect(() => {
    if (hasHandledReturnRef.current) return;

    if (!isReturnFromCheckout) return;

    hasHandledReturnRef.current = true;
    let cancelled = false;

    const runPostCheckoutSync = async () => {
      const maxAttempts = 8;
      const delayMs = 1500;
      setIsPolling(true);

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        if (cancelled) return;

        try {
          const latestData = await checkSubscription();
          const latestStatus = latestData?.subscription_status;
          const latestRequiresPaymentMethod = Boolean(
            latestData?.requires_payment_method
          );
          const canAccessApp =
            Boolean(latestData?.has_access) ||
            ((latestStatus === "active" || latestStatus === "trialing") &&
              !latestRequiresPaymentMethod);

          if (canAccessApp) {
            navigate(redirectPath, { replace: true });
            return;
          }
        } catch (_) {
          // Retry for transient webhook delays.
        }

        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }

      setIsPolling(false);
    };

    runPostCheckoutSync();

    return () => {
      cancelled = true;
      setIsPolling(false);
    };
  }, [isReturnFromCheckout, checkSubscription, navigate, redirectPath]);

  const handleGetStarted = async (planType, interval) => {
    if (isRedirecting) return;

    const activePlanKey = `${interval}_${planType}`;
    setActiveButtonKey(activePlanKey);
    setFrontendError("");

    setIsRedirecting(true);
    try {
      sessionStorage.setItem("pendingStripeCheckout", activePlanKey);
      sessionStorage.setItem("pendingStripeCheckoutAt", String(Date.now()));
    } catch (_) {
      // Non-blocking: continue redirect even if storage is unavailable.
    }

    try {
      const checkoutData = await createSubscription(planType, interval);

      const checkoutRedirectUrl =
        checkoutData?.checkout_url ||
        checkoutData?.checkoutUrl ||
        "";

      if (checkoutRedirectUrl) {
        window.location.assign(checkoutRedirectUrl);
        return;
      }

      throw new Error("Missing checkout_url in checkout session response.");
    } catch (error) {
      setIsRedirecting(false);
      setActiveButtonKey("");
      setFrontendError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to start checkout. Please try again."
      );
    }
  };

  const handleLogout = () => {
    setShowLogoutPopup(true);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutPopup(false);
    dispatch(logout());
    navigate("/login", { replace: true });
  };

  const handleGoToPayment = () => {
    setTimeout(() => {
      paymentPlansRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
  };

  return (
    <div
      className="min-h-screen font-mali overflow-y-auto px-4 pt-2 pb-4 md:pt-4 md:pb-6"
      style={{
        backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg w-full max-w-7xl mx-auto px-4 pt-4 pb-5 sm:px-6 md:px-8 lg:px-10 relative max-h-[calc(100vh-1rem)] md:max-h-[calc(100vh-1.5rem)] overflow-y-auto">
        <button
          type="button"
          onClick={handleLogout}
          className="absolute top-3 right-3 text-xs md:text-sm font-bold px-3 py-1.5 rounded-md border border-[#0B2B66] text-[#0B2B66] bg-white/90 hover:bg-white"
        >
          Logout
        </button>

        <h2 className="-mt-2 text-3xl md:text-5xl font-bold text-center mb-2">
          Free trial for 7 days
        </h2>
        <p className="text-center font-medium text-base md:text-xl mb-4 md:mb-6">
          Cancel anytime you want
        </p>

        <div className="mb-3 md:mb-4">
          <GlobalSubscriptionBanner />
        </div>

        <p className="text-center text-sm md:text-base mb-4 font-semibold">
          {trialText}
        </p>

        {(isChecking || isPolling) && (
          <p className="text-center text-sm text-blue-700 mb-4">
            Checking latest subscription status...
          </p>
        )}

        {frontendError && (
          <p className="text-center text-sm text-red-700 mb-4">{frontendError}</p>
        )}

        <div
          ref={paymentPlansRef}
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6 items-stretch"
        >
          {planCards.map((plan) => {
            const hasSubscribedPlan = Boolean(currentPlanKey);
            const cardActionKey = `${plan.interval}_${plan.planType}`;
            const cardPlanKey = `${plan.planType}_${plan.interval}`;
            const isCurrentPlan = currentPlanKey === cardPlanKey;
            const isCurrentActionLoading =
              (isRedirecting || isCreating) && activeButtonKey === cardActionKey;
            const isButtonDisabled = hasSubscribedPlan
              ? true
              : isRedirecting || isCreating;
            const buttonText = isCurrentActionLoading
              ? "Redirecting..."
              : isCurrentPlan
                ? "Subscribe"
                : "Get Started";
            const buttonClassName = isCurrentPlan
              ? "w-full mt-2 font-mulish font-extrabold tracking-wide text-lg py-2.5 px-3 rounded-xl border-2 border-[#0B2B66] text-[#0B2B66] bg-gradient-to-r from-[#FFF27A] via-[#FFBE4D] to-[#FF8F1F] shadow-[0_5px_0_#0B2B66,0_10px_20px_rgba(255,175,64,0.35)] cursor-not-allowed"
              : "text-primary-dark-blue border w-full mt-2 shadow-sm shadow-primary-dark-blue border-primary-dark-blue font-mulish font-bold text-lg py-2 px-3 rounded-lg hover:bg-black/5 transition disabled:opacity-60";

            return (
              <div
                key={plan.name}
                className="bg-white p-5 rounded-xl shadow-md text-left flex flex-col min-h-[360px]"
              >
                <h3 className="text-lg md:text-xl w-fit px-3 py-2 font-bold bg-[#D9EA49] rounded-md mb-3">
                  {plan.name}
                </h3>
                <p className="text-gray-700 mb-3 text-sm md:text-base leading-relaxed">
                  {plan.description}
                </p>
                <ul className="list-disc list-inside text-left text-primary-dark-blue font-medium mb-4 mt-1 text-sm md:text-base space-y-1">
                  {plan.benefits.map((benefit) => (
                    <li key={benefit}>{benefit}</li>
                  ))}
                </ul>
                <div className="mt-auto">
                  <p className="text-xs md:text-sm text-gray-600 font-semibold mb-1">
                    Billed in {plan.currency}
                  </p>
                  <div className="flex items-center justify-between">
                    <p className="text-sm md:text-base font-bold mb-3">
                      <span className="text-2xl">{plan.price}</span>{" "}
                      {plan.billingText}
                    </p>
                  </div>
                  <button
                    disabled={isButtonDisabled}
                    className={buttonClassName}
                    onClick={() => handleGetStarted(plan.planType, plan.interval)}
                  >
                    {buttonText}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-6 md:mt-8">
          <button
            disabled={isRedirecting}
            className="text-primary-dark-blue border-2 w-full max-w-md mt-2 shadow-sm border-primary-dark-blue font-mulish font-bold text-lg md:text-xl py-2.5 px-4 rounded-lg hover:bg-blue-50 transition disabled:opacity-60"
            onClick={handleGoToPayment}
          >
            Start my 7 day free trial
          </button>

          <button
            type="button"
            className="font-mulish block mt-2 text-xs md:text-sm text-gray-500 underline mx-auto"
            onClick={() => navigate("/subscription-terms")}
          >
            Read Subscription terms
          </button>
        </div>
      </div>

      {showLogoutPopup && (
        <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center px-4">
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6 text-center border-2 border-[#0B2B66]">
            <button
              type="button"
              aria-label="Close logout popup"
              onClick={() => setShowLogoutPopup(false)}
              className="absolute top-4 right-4 h-11 w-11 text-[#0B2B66] flex items-center justify-center text-2xl leading-none rounded-xl border-2 border-[#0B2B66] bg-gradient-to-br from-[#FFE066] via-[#FFB347] to-[#FF7AA2] shadow-[0_4px_0_#0B2B66] hover:brightness-105 active:translate-y-[1px] active:shadow-[0_2px_0_#0B2B66] transition"
            >
              &times;
            </button>

            <img
              src={GLOBAL_IMAGES.dingo}
              alt="MathDingo cartoon"
              className="w-24 h-24 mx-auto mb-4"
            />
            <h3 className="text-2xl font-bold text-[#0B2B66] mb-2">
              Are you sure?
            </h3>
            <p className="text-gray-700 font-semibold mb-5">
              Do you want to logout now?
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowLogoutPopup(false)}
                className="w-full py-2.5 rounded-lg border-2 border-[#0B2B66] text-[#0B2B66] font-bold hover:bg-blue-50 transition"
              >
                Stay Here
              </button>
              <button
                type="button"
                onClick={handleLogoutConfirm}
                className="w-full py-2.5 rounded-lg bg-gradient-to-r from-[#FFF27A] via-[#FFBE4D] to-[#FF8F1F] text-[#0B2B66] border-2 border-[#0B2B66] font-bold shadow-[0_4px_0_#0B2B66] hover:brightness-105 transition"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;
